import { API } from '@/modules/api'
import type { TMbaPowerschoolFeeCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { parse, format } from 'date-fns'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { useCartStore } from '@/stores/cart'
import { hasUnsignedForms } from '@/mixins/OtherFeeHelper'

export const createUniqueId = (feeId: string) => feeId + '::' + OrderItemTypeCode.MbaPowerschoolFee

export const useMbaPowerschoolFeeStore = defineStore('mbaPowerschoolFee', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    mbaFees: {} as Record<TStudentAccountId, TMbaPowerschoolFeeCartLine[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    getMbaFees(state) {
      return (studentAccountId: TStudentAccountId) => state.mbaFees[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const mbaFees = await API.Get.mbaPowerschoolFees(studentAccountId)
      this.mbaFees[studentAccountId] = mbaFees
      this.validateCart(districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the mba powerschool fees store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TMbaPowerschoolFeeCartLine[]) {
        if (
          line.orderItemType === OrderItemType.MbaPowerschoolFee &&
          line.studentAccountId === studentAccountId
        ) {
          const fee = this.mbaFees[studentAccountId].find(fee => fee.feeId === line.feeId)
          if (!fee) {
            console.log('mba powerschool fee not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          // DTA-659: same as the core PowerSchool store — the add-time gate cannot see
          // a line rehydrated from localStorage, and there is no server-side form check
          // for this order type behind it.
          if (hasUnsignedForms(fee)) {
            console.log('mba powerschool fee now requires an unsubmitted form', line.feeId)
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          // ensure fee amount is less than cart amount meaning cart amount is too high
          if (fee.amountDue < line.amount) {
            console.log(
              'mba powerschool fee amount is less than cart amount, adjusting cart',
              line.feeId
            )
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fee.amountDue
            })
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, feeData: Omit<TMbaPowerschoolFeeCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, { ...feeData, uniqueId })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TMbaPowerschoolFeeCartLine> & { feeId: string }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: string) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.MbaPowerschoolFee) {
          this.removeFromCart(districtId, (line as TMbaPowerschoolFeeCartLine).feeId)
        }
      }
    }
  }
})
